/**
 * Not Found Page
 *
 * Catch-all that React Router renders for any path not matched by the
 * routed pages. Server-side, Firebase Hosting has already 200-rewritten
 * to /index.html (standard SPA behavior), so this page renders inside
 * a 200 response — fine for users, slightly suboptimal for SEO crawlers.
 * The URLs we care about for SEO are all explicit routes; the few
 * external links that point at deprecated paths get 301-redirected at
 * the Firebase edge before they reach React Router.
 */

import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PageWrapper } from '@components/layout/page-wrapper';
import { GlassCard } from '@components/primitives/glass-card';
import { PrimaryButton, SecondaryButton } from '@components/primitives/glass-button';
import { IconWrapper } from '@components/primitives/icon';
import { Compass, ArrowLeft } from '@phosphor-icons/react';
import { colors } from '@config/seafoam_colors';
import { spacing, borderRadius } from '@config/spacing';
import { typography } from '@config/typography';
import { ROUTES } from '@routes/route-paths';

const NotFoundContainer = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing['5xl']} ${spacing.xl};
`;

const NotFoundCard = styled(GlassCard)`
  align-items: center;
  text-align: center;
  gap: ${spacing.xl};
  max-width: 520px;
  padding: ${spacing['4xl']};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.md};
  border-radius: ${borderRadius.full};
  background: rgba(255, 255, 255, 0.18);
  color: ${colors.textLight};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  letter-spacing: ${typography.letterSpacing.tight};
  text-transform: uppercase;
`;

const Headline = styled.h1`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['5xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: 0;
`;

const Description = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  color: ${colors.textSecondary};
  line-height: ${typography.lineHeight.bodyRelaxed};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${spacing.md};
  width: 100%;
`;

const SuggestionsLabel = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  color: ${colors.textSecondary};
  letter-spacing: ${typography.letterSpacing.tight};
  text-transform: uppercase;
  margin-top: ${spacing.lg};
`;

const SuggestionLinks = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${spacing.lg};
`;

const SuggestionLink = styled(Link)`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text};
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  padding-bottom: 2px;
  transition: opacity 0.15s ease, border-color 0.15s ease;

  &:hover {
    opacity: 0.85;
    border-bottom-color: rgba(255, 255, 255, 0.6);
  }
`;

/* Common destinations for someone who landed on a 404 — covers the most
   likely intents (return home, contact us, read the blog, find the press
   kit, look up legal docs). Order roughly by frequency. */
const SUGGESTIONS: Array<{ to: string; label: string }> = [
  { to: ROUTES.HOME, label: 'Home' },
  { to: ROUTES.CONTACT_US, label: 'Contact' },
  { to: ROUTES.AI_BLOGS, label: 'Blog' },
  { to: ROUTES.PRESS, label: 'Press' },
  { to: ROUTES.PRIVACY_POLICY, label: 'Privacy' },
];

export const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const previous = document.title;
    document.title = 'Page not found · The Home CEO';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <PageWrapper contained>
      <NotFoundContainer>
        <NotFoundCard>
          <Badge>404 — page not found</Badge>
          <IconWrapper $size={56} $color={colors.white}>
            <Compass weight="fill" />
          </IconWrapper>
          <Headline>We can&apos;t find that page</Headline>
          <Description>
            The link you followed may have moved, expired, or never existed.
            Most of what The Home CEO does now lives in the mobile app —
            but the pages below are still right here on the web.
          </Description>
          <Actions>
            <PrimaryButton type="button" onClick={() => navigate(ROUTES.HOME)}>
              Return home
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => navigate(-1)}>
              <IconWrapper $size={18}>
                <ArrowLeft weight="bold" />
              </IconWrapper>
              Go back
            </SecondaryButton>
          </Actions>
          <SuggestionsLabel>Or jump to</SuggestionsLabel>
          <SuggestionLinks aria-label="Suggested pages">
            {SUGGESTIONS.map(({ to, label }) => (
              <SuggestionLink key={to} to={to}>
                {label}
              </SuggestionLink>
            ))}
          </SuggestionLinks>
        </NotFoundCard>
      </NotFoundContainer>
    </PageWrapper>
  );
};
